import { ExclamationCircleFilled } from '@ant-design/icons';
import { Card, Col, Flex, Menu, Row, Tooltip, Typography, theme } from 'antd';
import type { MenuProps } from 'antd';
import type { ReactNode } from 'react';

export interface MasterDetailItem {
  key: number;
  // items with the same group are listed under one heading, in the order they are given
  group?: string;
  label: string;
  summary: string;
  // when set, the item is not ready yet and this says why
  warning?: string;
}

interface SetupMasterDetailProps {
  items: MasterDetailItem[];
  selectedKey: number;
  onSelect: (key: number) => void;
  title: string;
  subtitle?: string;
  children: ReactNode;
}

// The list of items on the left, with their status at a glance, and the editor of the selected one on the right
export const SetupMasterDetail = ({
  items,
  selectedKey,
  onSelect,
  title,
  subtitle,
  children,
}: SetupMasterDetailProps): ReactNode => {
  const { token } = theme.useToken();

  const toMenuItem = (
    item: MasterDetailItem
  ): NonNullable<MenuProps['items']>[number] => ({
    key: String(item.key),
    style: { height: 'auto', lineHeight: 1.4, padding: '8px 16px' },
    label: (
      <Flex justify="space-between" align="center" gap={8}>
        <div style={{ minWidth: 0 }}>
          <div style={{ whiteSpace: 'normal' }}>{item.label}</div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {item.summary}
          </Typography.Text>
        </div>
        {item.warning ? (
          <Tooltip title={item.warning}>
            <ExclamationCircleFilled style={{ color: token.colorWarning }} />
          </Tooltip>
        ) : null}
      </Flex>
    ),
  });

  const menuItems: MenuProps['items'] = [];
  let currentGroup: string | undefined;
  let groupChildren: MenuProps['items'] = [];
  const closeGroup = (): void => {
    if (currentGroup !== undefined && groupChildren) {
      menuItems.push({
        type: 'group',
        key: `group-${menuItems.length}`,
        // the group (department) heading stands out from the items under it
        label: (
          <span
            style={{
              display: 'block',
              padding: '4px 8px',
              borderRadius: token.borderRadiusSM,
              background: token.colorInfoBg,
              color: token.colorText,
              fontWeight: 600,
              fontSize: token.fontSize,
            }}
          >
            {currentGroup}
          </span>
        ),
        children: groupChildren,
      });
    }
    groupChildren = [];
  };

  items.forEach((item) => {
    if (item.group === undefined) {
      menuItems.push(toMenuItem(item));
      return;
    }
    if (item.group !== currentGroup) {
      closeGroup();
      currentGroup = item.group;
    }
    groupChildren?.push(toMenuItem(item));
  });
  closeGroup();

  return (
    <>
      <Row gutter={[24, 16]}>
        <Col xs={24} md={9} lg={8}>
          <Card
            size="small"
            styles={{ body: { padding: 0, maxHeight: 560, overflowY: 'auto' } }}
          >
            <Menu
              mode="inline"
              selectedKeys={[String(selectedKey)]}
              items={menuItems}
              onClick={({ key }) => onSelect(Number(key))}
              style={{ borderInlineEnd: 0 }}
            />
          </Card>
        </Col>
        <Col xs={24} md={15} lg={16}>
          <Typography.Title level={4} style={{ marginTop: 0, marginBottom: 0 }}>
            {title}
          </Typography.Title>
          {subtitle && (
            <Typography.Text type="secondary">{subtitle}</Typography.Text>
          )}
          <div style={{ marginTop: 16 }}>{children}</div>
        </Col>
      </Row>
    </>
  );
};
